import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { motion } from 'framer-motion';
import { format, parseISO, startOfWeek, subWeeks } from 'date-fns';
import { de } from 'date-fns/locale';
import { useNavigate } from 'react-router-dom';
import {
  Calendar,
  Check,
  Clock,
  X,
  AlertCircle,
  ChevronRight,
} from 'lucide-react';

/**
 * Compact overview of the employee's last N weeks with approval status
 */
export function MyWeeksOverview({ weeksCount = 12 }) {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [approvals, setApprovals] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchMyApprovals = async () => {
      try {
        const response = await api.get('/approvals/my');
        setApprovals(response.data || []);
      } catch (error) {
        console.error('Failed to fetch my approvals:', error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchMyApprovals();
  }, [api]);

  // Generate list of last N weeks (Monday start)
  const generateWeeks = () => {
    const weeks = [];
    const today = new Date();
    
    for (let i = 0; i < weeksCount; i++) {
      const weekStart = startOfWeek(subWeeks(today, i), { weekStartsOn: 1 });
      const weekStartStr = format(weekStart, 'yyyy-MM-dd');
      
      // Find approval for this week
      const approval = approvals.find(a => a.week_start === weekStartStr);
      
      weeks.push({
        weekStart: weekStartStr,
        weekStartDate: weekStart,
        kw: format(weekStart, 'w', { locale: de }),
        label: format(weekStart, 'd. MMM', { locale: de }),
        year: format(weekStart, 'yyyy'),
        status: approval?.status || 'open',
        approvalId: approval?.id,
        rejectionReason: approval?.rejection_reason,
      });
    }
    return weeks;
  };

  const weeks = generateWeeks();

  const getStatusConfig = (status) => {
    switch (status) {
      case 'approved':
        return {
          color: 'bg-emerald-500',
          borderColor: 'border-emerald-500/30',
          bgColor: 'bg-emerald-500/10',
          textColor: 'text-emerald-600 dark:text-emerald-400',
          icon: Check,
          label: 'Genehmigt',
        };
      case 'pending':
        return {
          color: 'bg-amber-500',
          borderColor: 'border-amber-500/30',
          bgColor: 'bg-amber-500/10',
          textColor: 'text-amber-600 dark:text-amber-400',
          icon: Clock,
          label: 'Wartet',
        };
      case 'rejected':
        return {
          color: 'bg-red-500',
          borderColor: 'border-red-500/30',
          bgColor: 'bg-red-500/10',
          textColor: 'text-red-600 dark:text-red-400',
          icon: X,
          label: 'Abgelehnt',
        };
      case 'fixed_by_supervisor':
        return {
          color: 'bg-indigo-500',
          borderColor: 'border-indigo-500/30',
          bgColor: 'bg-indigo-500/10',
          textColor: 'text-indigo-600 dark:text-indigo-400',
          icon: Check,
          label: 'Festgesetzt',
        };
      case 'final':
        return {
          color: 'bg-muted-foreground',
          borderColor: 'border-border/30',
          bgColor: 'bg-muted/50',
          textColor: 'text-muted-foreground',
          icon: Check,
          label: 'Final',
        };
      default: // 'open'
        return {
          color: 'bg-muted-foreground',
          borderColor: 'border-border',
          bgColor: 'bg-muted/50',
          textColor: 'text-muted-foreground',
          icon: AlertCircle,
          label: 'Offen',
        };
    }
  };

  const handleWeekClick = (week) => {
    // Navigate to time tracking page with the selected week
    navigate(`/time-tracking?week=${week.weekStart}`);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="w-6 h-6 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Count by status
  const statusCounts = weeks.reduce((acc, w) => {
    acc[w.status] = (acc[w.status] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="space-y-4">
      {/* Summary */}
      <div className="flex flex-wrap items-center gap-3 text-xs">
        {statusCounts.approved > 0 && (
          <span className="flex items-center gap-1 text-emerald-600 dark:text-emerald-400">
            <Check className="w-3 h-3" /> {statusCounts.approved} genehmigt
          </span>
        )}
        {statusCounts.pending > 0 && (
          <span className="flex items-center gap-1 text-amber-600 dark:text-amber-400">
            <Clock className="w-3 h-3" /> {statusCounts.pending} wartend
          </span>
        )}
        {statusCounts.rejected > 0 && (
          <span className="flex items-center gap-1 text-red-600 dark:text-red-400">
            <X className="w-3 h-3" /> {statusCounts.rejected} abgelehnt
          </span>
        )}
        {statusCounts.open > 0 && (
          <span className="flex items-center gap-1 text-muted-foreground">
            <AlertCircle className="w-3 h-3" /> {statusCounts.open} offen
          </span>
        )}
      </div>

      {/* Week Grid */}
      <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-12 gap-2">
        {weeks.map((week, index) => {
          const config = getStatusConfig(week.status);
          const StatusIcon = config.icon;
          
          return (
            <motion.button
              key={week.weekStart}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: index * 0.02 }}
              onClick={() => handleWeekClick(week)}
              className={`relative p-2 rounded-lg border ${config.borderColor} ${config.bgColor} hover:bg-accent transition-colors group`}
              title={`KW ${week.kw}: ${config.label}${week.rejectionReason ? ` - ${week.rejectionReason}` : ''}`}
              data-testid={`week-status-${week.weekStart}`}
            >
              {/* Status indicator dot */}
              <div className={`absolute top-1 right-1 w-2 h-2 rounded-full ${config.color}`} />
              
              {/* Week info */}
              <div className="text-center">
                <p className={`text-xs font-medium ${config.textColor}`}>
                  KW {week.kw}
                </p>
                <p className="text-[10px] text-muted-foreground mt-0.5">
                  {week.label}
                </p>
              </div>
              
              {/* Hover indicator */}
              <ChevronRight className="w-3 h-3 text-muted-foreground absolute bottom-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity" />
            </motion.button>
          );
        })}
      </div>

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-4 text-[10px] text-muted-foreground pt-2 border-t border-border">
        <div className="flex items-center gap-1">
          <div className="w-2 h-2 rounded-full bg-emerald-500" />
          <span>Genehmigt</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-2 h-2 rounded-full bg-amber-500" />
          <span>Wartend</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-2 h-2 rounded-full bg-red-500" />
          <span>Abgelehnt</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-2 h-2 rounded-full bg-muted-foreground" />
          <span>Offen</span>
        </div>
      </div>
    </div>
  );
}

export default MyWeeksOverview;
